import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { powerPlantDetailAPI, subscriptionAPI } from '../services/api';
import {
  buildSubscriptionMap,
  countActiveSubscriptions,
  getMinRemainingDays,
  isSubscriptionActive,
  normalizeSubscriptions,
} from '../utils/subscription';

const SubscriptionContext = createContext({
  subscriptions: [],
  connectedPlants: [],
  subscriptionMap: new Map(),
  remainingDays: null,
  activeSubscriptionCount: 0,
  connectedPlantCount: 0,
  isLoading: false,
  isResolved: false,
  requiresPurchase: false,
  getSubscriptionForPlant: () => null,
  hasActiveSubscriptionForPlant: () => false,
  refreshSubscription: () => {},
});

export const SubscriptionProvider = ({ user, children }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [connectedPlants, setConnectedPlants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isResolved, setIsResolved] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const isAdmin = user?.role === 'ADMIN';

  const refreshSubscription = useCallback(() => {
    if (!user || isAdmin) return;
    setReloadKey((prev) => prev + 1);
  }, [user, isAdmin]);

  useEffect(() => {
    if (!user) {
      setSubscriptions([]);
      setConnectedPlants([]);
      setIsLoading(false);
      setIsResolved(false);
      return;
    }

    if (isAdmin) {
      setSubscriptions([]);
      setConnectedPlants([]);
      setIsLoading(false);
      setIsResolved(true);
      return;
    }

    let cancelled = false;

    const loadSubscriptionData = async () => {
      setIsLoading(true);
      setIsResolved(false);

      try {
        const [subscriptionData, plantResponse] = await Promise.all([
          subscriptionAPI.getMySubscriptions(),
          powerPlantDetailAPI.getDetailsList({
            sortBy: 'createdAt',
            sortDirection: 'DESC',
            limit: 200,
          }),
        ]);

        if (!cancelled) {
          setSubscriptions(normalizeSubscriptions(subscriptionData));
          const plants = (plantResponse?.data || []).map((plant) => ({
            id: plant.id,
            name: plant.name,
          }));
          setConnectedPlants(plants);
        }
      } catch (error) {
        if (!cancelled) {
          setSubscriptions([]);
          setConnectedPlants([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsResolved(true);
        }
      }
    };

    loadSubscriptionData();

    return () => {
      cancelled = true;
    };
  }, [user, isAdmin, reloadKey]);

  const subscriptionMap = useMemo(
    () => buildSubscriptionMap(subscriptions),
    [subscriptions]
  );

  const getSubscriptionForPlant = useCallback(
    (powerLocationId) => subscriptionMap.get(Number(powerLocationId)) ?? null,
    [subscriptionMap]
  );

  const hasActiveSubscriptionForPlant = useCallback(
    (powerLocationId) => isSubscriptionActive(getSubscriptionForPlant(powerLocationId)),
    [getSubscriptionForPlant]
  );

  const activeSubscriptions = useMemo(
    () => subscriptions.filter(isSubscriptionActive),
    [subscriptions]
  );

  const activeSubscriptionCount = useMemo(
    () => countActiveSubscriptions(subscriptions),
    [subscriptions]
  );

  const connectedPlantCount = connectedPlants.length;

  const remainingDays = useMemo(
    () => getMinRemainingDays(activeSubscriptions),
    [activeSubscriptions]
  );

  const value = useMemo(() => {
    const hasAnyActiveSubscription =
      isResolved && activeSubscriptionCount > 0;

    return {
      subscriptions,
      connectedPlants,
      subscriptionMap,
      remainingDays,
      activeSubscriptionCount,
      connectedPlantCount,
      isLoading: Boolean(user) && !isAdmin && isLoading,
      isResolved: Boolean(user) && (isAdmin || isResolved),
      requiresPurchase:
        Boolean(user) &&
        !isAdmin &&
        isResolved &&
        connectedPlantCount > 0 &&
        !hasAnyActiveSubscription,
      getSubscriptionForPlant,
      hasActiveSubscriptionForPlant,
      refreshSubscription,
    };
  }, [
    user,
    isAdmin,
    subscriptions,
    connectedPlants,
    subscriptionMap,
    remainingDays,
    activeSubscriptionCount,
    connectedPlantCount,
    isLoading,
    isResolved,
    getSubscriptionForPlant,
    hasActiveSubscriptionForPlant,
    refreshSubscription,
  ]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
